import { useEffect, useRef, useState } from "react";
import Button from "@/common/elements/Button";
import DataTable from "@/common/elements/DataTable";
import { StyledFlex, 
    StyledGridCol, 
    StyledGridRow, 
    StyledSectionHeading, 
    StyledSubTableContainer 
} from "@/common/styles";
import CustomModal from "@/components/CustomModal";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import TextField from "@/common/elements/InputElements/TextField";
import CashAccountPaymentForm from "./CashAccountPaymentForm";
import Checkbox from "@/common/elements/InputElements/CheckBox";
import Select from "@/common/elements/InputElements/Select";
import CashAccountFeeOrFineForm from "./CashAccountDetailForm";
import CashAccountDetailForm from "./CashAccountDetailForm";
import dayjs from "dayjs";
import useActiveWarrant from "@/hooks/useActiveWarrant";
import { isNonEmptyVariable, getRefListNameByID, getOptionsFromRefList } from "@/util/common_function";

const CashAccountDetails = ({}) => {
    const { activeWarrant, patchArray, bookingsFieldsEnabled } = useActiveWarrant();

    const inmateCashAccountRef = {
        cashDeposit: useRef(),
        purchase: useRef(),
    };

    const [selectedData, setSelectedData] = useState();
    const [selectedIndex, setSelectedIndex] = useState();

    const formatSerial = (index) => String(index + 1).padStart(3, "0");
    const [transactionMode,setTransactionMode] = useState(null);
    const [isPartialPayments,setIsPartialPayments] = useState(null);

    const [detailsModal,setDetailsModal] = useState(false);
    const [paymentModal,setPaymentModal] = useState(false);
    const [feeOrFineModal,setFeeOrFineModal] = useState(false);

    const [tableData, setTableData] = useState({
        headers:[
            {key:"id",text:"#"},
            {key:"dateTime",text:"Date Time"},
            {key:"user",text:"User"},
            {key:"category",text:"Category"},
            {key:"amount",text:"Amount"},
            {key:"transactionDetails",text:"Transaction Details"},
            {key:"receiptNumber",text:"Receipt Number"},
            {key:"transactionType",text:"Transaction Type"}
        ],
        rows:[]
    });

    const tableHeaders = [
        { key: "id", text: "#" },
        { key: "dateTime", text: "Date Time" },
        {
            key: "user",
            text: "User",
            render: (text) => {
            const staffID = !isNaN(text) ? parseInt(text) : null;
            if (isNonEmptyVariable(staffID))
                return `${getRefListNameByID("", staffID, true, true)}`;
            return `${text}`;
            },
        },
        { key: "category", text: "Category" },
        { key: "amount", text: "Amount" },
        { key: "transactionDetails", text: "Transaction Details" },
        { key: "receiptNumber", text: "Receipt Number" },
        { key: "transactionType", text: "Transaction Type" },
    ];
    
    const handlePaymentModal = () =>{
        setPaymentModal(!paymentModal)
    }

    const handleFeeOrFineModal = () =>{
        setFeeOrFineModal(!feeOrFineModal)
    }

    const handleDetailsModal = () => {
         setDetailsModal(!detailsModal)
    }
    const handlePartialPaymentChange = (checkbox) =>{
        const isChecked = checkbox?.target?.checked;
        setIsPartialPayments(isChecked)
        patchField("juvenileWarrant", isChecked);
    }

    const getTransactions = () =>
        activeWarrant?.warrantCashAccount?.[0]
            ?.cashAccountingNew?.[0]
            ?.cashAccountingNewTransactions || [];

    const handleRowClick = (value, index) => {
        if (value) {
            setSelectedData(value);
            setSelectedIndex(index);
        }

        if (index !== undefined && index !== null) {
            setDetailsModal(true);
        }
    };

    const handleOnSave = async () => {
        let values;

        try {
            if (paymentModal) {
            await inmateCashAccountRef.cashDeposit.current.validateFields();

            values =
                inmateCashAccountRef.cashDeposit.current.getFormValues();

            values.transactionType = "Credit";
            } else {
            await inmateCashAccountRef.purchase.current.validateFields();

            values =
                inmateCashAccountRef.purchase.current.getFormValues();

            values.transactionType = "Debit";
            }
        } catch {
            return;
        }

        const cashAccounts = structuredClone(
            activeWarrant?.warrantCashAccount || []
        );

        if (!cashAccounts[0]) {
            cashAccounts.push({
                warrantID:
                    activeWarrant?.warrantId ??
                    activeWarrant?.warrantID ??
                    null,
                cashAccountingNew: [
                    {
                    cashAccountingNewTransactions: [],
                    },
                ],
            });
        }

        if (!cashAccounts[0].cashAccountingNew?.[0]) {
            cashAccounts[0].cashAccountingNew = [
            {
                cashAccountingNewTransactions: [],
            },
            ];
        }

        const transactions =
            cashAccounts[0].cashAccountingNew[0]
            .cashAccountingNewTransactions || [];

        const userDetails = JSON.parse(
            localStorage.getItem("userDetails")
        );

        transactions.push({
            ...values,
            cashAccountingTransactionId: null,
            amount: parseFloat(values.amount) || 0,
            user: values.receivedBy || null,
            receivedBy: values.receivedBy || null,
            receivedByStaffId: values.receivedBy || null,
            creatorStaffId:
            userDetails?.staffId ||
            userDetails?.staffID ||
            null,
            dateAdded: dayjs().format("MM/DD/YYYY"),
            timeAdded: dayjs().format("HH:mm"),
            dateTime: dayjs().format("MM/DD/YYYY HH:mm"),
        });

        cashAccounts[0].cashAccountingNew[0]
            .cashAccountingNewTransactions = transactions;

        patchArray("warrantCashAccount", cashAccounts);

        setPaymentModal(false);
        setFeeOrFineModal(false);
    };

    const handleDelete = () => {
        if (!selectedData) return;

        const cashAccounts = structuredClone(
            activeWarrant?.warrantCashAccount || []
        );

        const transactions =
            cashAccounts?.[0]?.cashAccountingNew?.[0]
            ?.cashAccountingNewTransactions || [];

        cashAccounts[0].cashAccountingNew[0]
            .cashAccountingNewTransactions =
            transactions.filter(
                (tx) =>
                tx.cashAccountingTransactionId !==
                selectedData.cashAccountingTransactionId
            );

        patchArray("warrantCashAccount", cashAccounts);

        setSelectedData(null);
        setSelectedIndex(null);
    };

    const balance = getTransactions().reduce((acc, tx) => {
        const amt =
            parseFloat(tx?.amount?.parsedValue ?? tx?.amount) || 0;

        return tx?.transactionType === "Credit"
            ? acc + amt
            : acc - amt;
    }, 0);

    return(
        <>
        <StyledSectionHeading marginBottom="0px" marginTop="20px">
            <span>Cash Account</span>
        </StyledSectionHeading>
        
        <StyledGridRow gutter={16}>

             <StyledGridCol marginBottom="15px">
                <FormItem 
                    label="Partial payments?" 
                    labelPostion="label-right"
                    layout="horizontal" 
                    paddingTop="15px"
                    name="partialPayments"
                >
                   <Checkbox onChange={handlePartialPaymentChange} />
                </FormItem>
             </StyledGridCol>

             { isPartialPayments && (
             <>
             <StyledGridCol lg={6}>
                <FormItem label="Payment Amount">
                   <TextField />
                </FormItem>
             </StyledGridCol>

             <StyledGridCol lg={6}>
                <FormItem label="Payment Period">
                   <Select 
                        options={getOptionsFromRefList("Payment_Period")}
                   />
                </FormItem>
             </StyledGridCol>
             </>
            
            )}

        </StyledGridRow>
        
        <StyledSubTableContainer>
            <StyledFlex justify="space-between" align="center" marginBottom="5px">
                <h2 style={{margin:"0"}}>Account Transactions</h2>
                <StyledFlex gap="10px">
                    <Button 
                        onClick={handlePaymentModal}
                        disabled={!bookingsFieldsEnabled}
                    >
                        Payment
                    </Button>
                    <Button 
                        onClick={handleFeeOrFineModal}
                        disabled={!bookingsFieldsEnabled}
                    >
                        Fee/Fine
                    </Button>
                    <Button
                        onClick={handleDetailsModal}
                        disabled={!selectedData}
                    >
                        Details
                    </Button>

                    <Button
                        onClick={handleDelete}
                        disabled={!selectedData || !bookingsFieldsEnabled}
                    >
                        Delete
                    </Button>
                </StyledFlex>
            </StyledFlex>

            <DataTable
                headers={tableHeaders}
                data={getTransactions().map((tx, index) => ({
                    ...tx,
                    id: formatSerial(index),
                    dateTime:
                        tx?.dateAdded && tx?.timeAdded
                        ? `${tx.dateAdded} ${tx.timeAdded}`
                        : tx?.dateAdded || null,
                    amount: tx?.amount?.parsedValue ?? tx?.amount,
                }))}
                onRowClick={handleRowClick}
                rowUniqueId="cashAccountingTransactionId"
                totalRecords={getTransactions().length}
                selectionColumnVisible={false}
            />
            <div style={{ textAlign: "right" }}>
                <span>
                    <b>Balance:</b> ${balance.toFixed(2)}
                </span>
            </div>
        </StyledSubTableContainer>

        <StyledGridRow gutter={16}>
            <StyledGridCol lg={24} marginTop="15px">
                <FormItem label="Comments">
                    <TextField inputType="textarea" rows="2" disabled={!bookingsFieldsEnabled}/>
                </FormItem>
            </StyledGridCol>
        </StyledGridRow>

        { detailsModal && (
        <CustomModal
        show={detailsModal}
        onHide={()=> setDetailsModal(false)}
        header="Cash Account Transaction"
        size="lg"
        >
           <CashAccountDetailForm
                purchaseFormRef={inmateCashAccountRef.purchase}
                initialData={selectedData}
                viewOnly={true}
            />
        </CustomModal>
        )}

        { paymentModal && (
        <CustomModal
        show={paymentModal}
        onHide={()=> setPaymentModal(false)}
        header="Enter New Payment For This Account"
        size="lg"
        onSaveClick={handleOnSave}
        >
            <CashAccountPaymentForm
                depositFormRef={inmateCashAccountRef.cashDeposit}
            />

        </CustomModal>
        )}

        { feeOrFineModal && (
        <CustomModal
        show={feeOrFineModal}
        onHide={()=> setFeeOrFineModal(false)}
        header="Enter New Fee/Fine For This Account"
        size="lg"
        onSaveClick={handleOnSave}
        >
            <CashAccountDetailForm
                purchaseFormRef={inmateCashAccountRef.purchase}
            />

        </CustomModal>
        )}

        </>
    )
}
export default CashAccountDetails;